import type { AuditEvent, AuditPage, AuditQuery } from './api'

// Reading the audit trail a page at a time, for one filter at a time.
//
// # WHY THIS IS NOT A HOOK
//
// It was one, inside views/Audit.tsx, and it was right. But a hook runs only in
// a browser, so nothing in the test suite could see the two things it exists to
// get right: a page that lands late must not be shown under a filter chosen
// since, and the rows on the screen must be gone the moment the filter changes.
// Two reviews of this screen (11 October 2026) each found a way the second had
// been lost, and each had passed every test. So the reading is plain code that
// a test can drive with an API it answers when it chooses, and the hook is the
// few lines that connect it to a component.

// The largest page the API serves is 200. Fifty is what it defaults to and what
// a reader gets through before deciding whether to go further back.
export const TRAIL_PAGE = 50

export type TrailState = {
  // The filter these rows answer, as its serialised query. Rows are handed to
  // a screen only for the filter the screen is on (trailFor), so there is no
  // moment at which one filter's rows sit under another's chips.
  key: string
  events: AuditEvent[]
  // The first page's head, which is where total, basis and the API's own note
  // for this filter live. The later pages repeat them.
  head: AuditPage | null
  // The cursor for the next read. Zero means the last page reached the start of
  // the trail; the API reports it rather than leaving it to be guessed from a
  // short page, so it can be trusted.
  nextBefore: number
  error: unknown
  loading: boolean
}

// A trail that is being read and holds nothing yet. Not the same as a trail
// that was read and is empty, which has a head.
export function emptyTrail(key: string): TrailState {
  return { key, events: [], head: null, nextBefore: 0, error: null, loading: true }
}

// What a screen on this filter may draw. A component renders once with its new
// filter before the read for it has even started, and in that render the state
// in hand is still the last filter's answer. This is what stops it being drawn:
// under a vendor's History that would be another vendor's acts.
export function trailFor(state: TrailState, key: string): TrailState {
  return state.key === key ? state : emptyTrail(key)
}

// Whether a filter, by its key, names one record. The API answers such a
// filter with each act as it was written and says so in its note; the screen
// prints that note above the rows instead of keeping it behind an (i).
export function namesOneRecord(key: string): boolean {
  try {
    return Boolean((JSON.parse(key) as AuditQuery).entityId)
  } catch {
    return false
  }
}

export type Trail = {
  state: () => TrailState
  // Told on every change. Returns the way to stop being told.
  subscribe: (listener: () => void) => () => void
  // Read this filter from its newest act. key is the query, serialised.
  start: (key: string) => void
  // The next page back of the filter being read.
  further: () => void
  // The screen has gone: nothing that lands after this is published.
  stop: () => void
}

export function createTrail(read: (q: AuditQuery) => Promise<AuditPage>): Trail {
  let state = emptyTrail('')
  const listeners = new Set<() => void>()
  // Kept here rather than read off the published state. further has to test
  // the cursor before it decides whether to fetch, and a value a component
  // closed over at its last render is a page behind the moment somebody clicks
  // twice quickly. The generation is what stops a page asked for under the
  // previous filter from being shown, or appended, under the new one when it
  // lands late.
  let cursor = 0
  let reading = false
  let generation = 0
  const put = (next: TrailState) => {
    state = next
    for (const told of listeners) told()
  }

  return {
    state: () => state,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },

    start(key) {
      const mine = ++generation
      cursor = 0
      reading = true
      // The last filter's rows, count and "nothing earlier" line go now, not
      // when the answer lands. A read has no time limit, and until it answers
      // they would stand under the new filter as its answer.
      put(emptyTrail(key))
      read({ ...(JSON.parse(key) as AuditQuery), limit: TRAIL_PAGE })
        .then((page) => {
          if (mine !== generation) return
          cursor = page.nextBefore ?? 0
          put({ key, events: page.events, head: page, nextBefore: cursor, error: null, loading: false })
        })
        .catch((err) => {
          if (mine === generation) put({ ...emptyTrail(key), error: err, loading: false })
        })
        .finally(() => {
          if (mine === generation) reading = false
        })
    },

    further() {
      if (reading || cursor === 0) return
      const mine = generation
      const before = cursor
      const key = state.key
      reading = true
      put({ ...state, loading: true })
      read({ ...(JSON.parse(key) as AuditQuery), limit: TRAIL_PAGE, before })
        .then((page) => {
          if (mine !== generation) return
          cursor = page.nextBefore ?? 0
          put({ ...state, events: [...state.events, ...page.events], nextBefore: cursor, error: null, loading: false })
        })
        .catch((err) => {
          if (mine === generation) put({ ...state, error: err, loading: false })
        })
        .finally(() => {
          if (mine === generation) reading = false
        })
    },

    stop() {
      generation++
    },
  }
}
